import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IoSend } from "react-icons/io5";
import { MdCancel } from "react-icons/md";

import { OthersAction, GptMessageAction } from "../../store/actions";

import { Spacer, boxContainer, GetProPopup, Loader } from "../../components";
import { Images } from "../../config";

import "./styles.css";

const API_KEY = process.env.REACT_APP_CHATGPT_API_KEY;
const systemMessage = {
  role: "system",
  content:
    "Explain things like you're talking to a school student of grade 11.",
};

const Essay = () => {
  const dispatch = useDispatch();
  const chatContainerRef = useRef(null);

  const profileData = useSelector((state) => state?.profile?.user);
  const { gptMessages, isLoading } = useSelector((state) => state?.gptMessages);

  const [typing, setIsTyping] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [showPreset, setShowPreset] = useState(true);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, typing]);

  useEffect(() => {
    dispatch(GptMessageAction.getGptMessage());
  }, []);

  useEffect(() => {
    setMessages(gptMessages);
  }, [gptMessages]);

  const handleSend = async () => {
    setShowPreset(false);
    if (!profileData?.freeUser) {
      if (!message?.trim()) return;

      dispatch(
        OthersAction.updateEssayGraderCount(() => {
          dispatch(OthersAction.getTasks());
        })
      );

      const newMessage = {
        message: message?.replace(/<[^>]+>/g, ""),
        sender: "user",
      };

      dispatch(GptMessageAction.addGptMessage(newMessage));

      const newMessages = [...messages, newMessage];

      setMessages(newMessages);

      setIsTyping(true);
      setMessage("");

      await processMessageToChatGPT(newMessages);
    }
  };

  const processMessageToChatGPT = async (chatMessages) => {
    let apiMessages = chatMessages.map((messageObject) => {
      let role = "";
      if (messageObject.sender === "ChatGPT") {
        role = "assistant";
      } else {
        role = "user";
      }
      return {
        role: role,
        content: messageObject.message,
      };
    });

    const apiRequestBody = {
      model: "gpt-3.5-turbo",
      messages: [systemMessage, ...apiMessages],
    };

    await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + API_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(apiRequestBody),
    })
      .then((data) => {
        return data.json();
      })
      .then((data) => {
        setMessages([
          ...chatMessages,
          {
            message: data.error
              ? data.error.message
              : data.choices[0].message.content,
            sender: "ChatGPT",
            direction: "incoming",
          },
        ]);
        dispatch(
          GptMessageAction.addGptMessage({
            message: data.error
              ? data.error.message
              : data.choices[0].message.content,
            sender: "ChatGPT",
          })
        );
        setIsTyping(false);
      });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  const prompts = [
    {
      value:
        "Does my essay line up with the mission statement of (specific university's business/art school name). Please give feedback.",
      heading: "Please give feedback",
      subHeading:
        "Does my essay line up with the mission statement of (specific university's business/art school name)",
    },
    {
      value:
        "I'm applying to (university), how I can change my essay to increase my chances of acceptance? Give me ideas.",
      heading: "Give me ideas",
      subHeading:
        "I'm applying to (university), how I can change my essay to increase my chances of acceptance?",
    },
    {
      value:
        "From the perspective of a university admissions officer, how can I improve this essay? Please give feedback.",
      heading: "Please give feedback",
      subHeading:
        "From the perspective of a university admissions officer, how can I improve this essay?",
    },
    {
      value:
        "Suggest how I can improve my writing to reflect (university name)'s values. Give me ideas.",
      heading: "Give me ideas",
      subHeading:
        "Suggest how I can improve my writing to reflect (university name)'s values.",
    },
  ];

  return (
    <div className="text-black">
      <div className="header-md">Automated Essay Grading</div>
      <Spacer height={20} />
      <boxContainer.LiteBoxContainer>
        <div className={`essay-content-container relative`}>
          {profileData?.freeUser && (
            <div className="absolute top-[100px] left-[35%] z-[10]">
              <GetProPopup />
            </div>
          )}
          {isLoading ? (
            <Loader.Circular />
          ) : (
            <div
              className={`${
                profileData?.freeUser && "blur pointer-events-none"
              } h-full flex flex-col justify-between`}
            >
              <div className="header-sm text-center w-[65%] mx-auto">
                Use the power of our model-trained essay grader to have your
                university essays graded by Artificial Intelligence.
              </div>

              <div className="w-[80%] mx-auto relative">
                {showPreset && (
                  <div className="grid grid-cols-2 gap-[0.3125em] w-full justify-center absolute z-[20] bottom-[5em] p-[0.3125em] backdrop-blur-md">
                    {/* <div className="absolute top-0 -right-[0.125em] bg-white">
                    <MdCancel
                      className="text-[1.25em] text-red-500 cursor-pointer"
                      onClick={() => setShowPreset(false)}
                    />
                  </div> */}
                    {prompts.map((item, index) => (
                      <div
                        className="p-3 rounded-2xl border border-main bg-[#fcfffe] cursor-pointer"
                        key={index}
                        onClick={() => {
                          setMessage(item.value);
                          setShowPreset(false);
                        }}
                      >
                        <div className="font-semibold text-[1.125em]">
                          {item?.heading}
                        </div>
                        <div>{item?.subHeading}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="absolute w-full flex justify-center mt-[9.375em]">
                  <img
                    src={Images.LogoIconOnly}
                    className="h-[11.25em] opacity-15"
                  />
                </div>
                <div
                  className={`chat-container ${showPreset && "pb-[13em]"}`}
                  ref={chatContainerRef}
                >
                  {messages?.map((item, index) => (
                    <span
                      key={index}
                      className={
                        item?.sender === "user"
                          ? "outgoing-chat"
                          : "incoming-chat"
                      }
                    >
                      {item?.message.split("\n").map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </span>
                  ))}
                  {typing && (
                    <span className="incoming-chat">Analyzing...</span>
                  )}
                </div>
                <div className="message-input-container">
                  <input
                    className="message-input"
                    placeholder="Enter your essay"
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyPress}
                    value={message}
                    disabled={profileData?.freeUser}
                  />
                  <div className="message-send-button" onClick={handleSend}>
                    <IoSend />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </boxContainer.LiteBoxContainer>
    </div>
  );
};

export default Essay;
